import { EsitoSms } from "@/components/SmsCrediti";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Search, Loader2, Send, Link2, Copy, Ban, Trash2, PenSquare,
  FileText, User, Plus, X, ShieldCheck, Eye, Mail, MessageSquare, Clock,
} from "lucide-react";

const STATUS_META = {
  BOZZA: { label: "Bozza", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  IN_ATTESA: { label: "In attesa di firma", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  FIRMATA: { label: "Firmata", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  RIFIUTATA: { label: "Rifiutata", cls: "bg-red-100 text-red-700 border-red-200" },
  SCADUTA: { label: "Scaduta", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  ANNULLATA: { label: "Annullata", cls: "bg-red-100 text-red-700 border-red-200" },
};

const copyText = async (t) => {
  try {
    await navigator.clipboard.writeText(t);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = t; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); ta.remove();
      return true;
    } catch { return false; }
  }
};

export default function SignTransactions() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const statusFilter = params.get("status") || "";

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [doc, setDoc] = useState(null);
  const [level, setLevel] = useState("OTP");
  const [idQuery, setIdQuery] = useState("");
  const [idResults, setIdResults] = useState([]);
  const [docQuery, setDocQuery] = useState("");
  const [docResults, setDocResults] = useState([]);

  const [linkDialog, setLinkDialog] = useState(null); // { url, id }
  const [notifySms, setNotifySms] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifying, setNotifying] = useState(false);
  const [remEnabled, setRemEnabled] = useState(true);
  const [remDays, setRemDays] = useState(3);
  const [remMax, setRemMax] = useState(2);
  const [savingRem, setSavingRem] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async (search = "", status = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/sign/transactions", { params: { q: search, status } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(q, statusFilter); }, [load, statusFilter]); // eslint-disable-line

  const displayIdentity = (it) =>
    [it.nome, it.cognome].filter(Boolean).join(" ") || it.ragione_sociale || it.identity_id;

  const searchIdentity = async () => {
    if (!idQuery.trim()) return;
    try {
      const { data } = await api.get("/sign/identities", { params: { q: idQuery } });
      setIdResults(data);
      if (!data.length) toast.info("Nessuna identity trovata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const searchDoc = async () => {
    if (!docQuery.trim()) return;
    try {
      const { data } = await api.get("/sign/documents", { params: { q: docQuery } });
      setDocResults(data);
      if (!data.length) toast.info("Nessun documento trovato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openCreate = () => {
    setIdentity(null); setDoc(null); setLevel("OTP");
    setIdQuery(""); setIdResults([]); setDocQuery(""); setDocResults([]);
    setDialogOpen(true);
  };

  const create = async () => {
    if (!identity || !doc) { toast.error("Seleziona cliente e documento"); return; }
    setCreating(true);
    try {
      await api.post("/sign/transactions", { identity_id: identity.identity_id, document_id: doc.document_id, verification_level: level });
      toast.success("Pratica di firma creata (bozza)");
      setDialogOpen(false);
      load(q, statusFilter);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const downloadSigned = async (tx) => {
    try {
      const { data } = await api.get(`/sign/transactions/${tx.transaction_id}/signed-document`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${tx.transaction_id}_firmato.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Documento firmato non disponibile");
    }
  };

  const buildUrl = (token) => `${window.location.origin}/sign/${token}`;

  const send = async (tx) => {
    try {
      const { data } = await api.post(`/sign/transactions/${tx.transaction_id}/send`, {
        base_url: window.location.origin,
        reminder_enabled: remEnabled, reminder_days: Number(remDays) || 3, reminder_max: Number(remMax) || 0,
      });
      const url = buildUrl(data.token);
      await copyText(url);
      setLinkDialog({ url, id: tx.transaction_id });
      toast.success("Link di firma generato e copiato");
      load(q, statusFilter);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveReminder = async () => {
    if (!linkDialog?.id) return;
    setSavingRem(true);
    try {
      await api.post(`/sign/transactions/${linkDialog.id}/reminder-config`, {
        enabled: remEnabled, days: Number(remDays) || 3, max: Number(remMax) || 0,
      });
      toast.success(remEnabled ? `Promemoria attivo: ogni ${remDays} giorni (max ${remMax})` : "Promemoria disattivati");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingRem(false); }
  };

  const copyLink = async (tx) => {
    try {
      const { data } = await api.get(`/sign/transactions/${tx.transaction_id}`);
      const token = data.transaction?.token;
      if (!token) { toast.error("Link non disponibile"); return; }
      const url = buildUrl(token);
      const ok = await copyText(url);
      setLinkDialog({ url, id: tx.transaction_id });
      if (ok) toast.success("Link copiato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doCancel = async () => {
    if (!cancelTarget) return;
    try {
      await api.post(`/sign/transactions/${cancelTarget.transaction_id}/cancel`);
      toast.success("Pratica annullata");
      setCancelTarget(null);
      load(q, statusFilter);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/sign/transactions/${deleteTarget.transaction_id}`);
      toast.success("Pratica eliminata");
      setDeleteTarget(null);
      load(q, statusFilter);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const notifyClient = async () => {
    if (!linkDialog?.id) return;
    const channels = [];
    if (notifySms) channels.push("sms");
    if (notifyEmail) channels.push("email");
    if (!channels.length) { toast.error("Seleziona almeno un canale"); return; }
    setNotifying(true);
    try {
      const { data } = await api.post(`/sign/transactions/${linkDialog.id}/notify`, { channels, base_url: window.location.origin });
      const r = data.result || {};
      const msgs = [];
      let anyFail = false;
      if (r.sms) { msgs.push(r.sms.sent ? `SMS inviato (${r.sms.to})` : `SMS non inviato: ${r.sms.reason || "errore"}`); anyFail = anyFail || !r.sms.sent; }
      if (r.email) { msgs.push(r.email.sent ? `Email inviata (${r.email.to})` : `Email non inviata: ${r.email.reason || "errore"}`); anyFail = anyFail || !r.email.sent; }
      (anyFail ? toast.warning : toast.success)(msgs.join(" · ") || "Inviato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setNotifying(false); }
  };

  const openDetail = async (tx) => {
    setDetail({ transaction: tx, events: [] });
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/sign/transactions/${tx.transaction_id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setDetailLoading(false); }
  };

  const setStatusFilter = (s) => {
    if (s) params.set("status", s); else params.delete("status");
    setParams(params, { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sign-transactions-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => nav("/assietica-sign")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="sign-tx-back-btn">
          <ArrowLeft className="h-4 w-4" /> Assietica Sign
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
              <PenSquare className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900">Pratiche di firma</h1>
              <p className="text-sm text-slate-500">Collega cliente e documento, invia il link e monitora lo stato</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="sign-new-tx-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nuova pratica
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button onClick={() => setStatusFilter("")} className={`text-xs px-3 py-1.5 rounded-full border ${!statusFilter ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`} data-testid="sign-tx-filter-all">Tutte</button>
          {Object.keys(STATUS_META).map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border ${statusFilter === s ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`sign-tx-filter-${s}`}>
              {STATUS_META[s].label}
            </button>
          ))}
        </div>

        <div className="relative max-w-md mb-4">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q, statusFilter)}
            placeholder="Cerca per ID, cliente o documento…" className="pl-10 bg-white" data-testid="sign-tx-search" />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-400" data-testid="sign-tx-loading">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-slate-400" data-testid="sign-tx-empty">
              Nessuna pratica di firma. Creane una con "Nuova pratica".
            </div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="sign-tx-list">
              {items.map((tx) => {
                const sm = STATUS_META[tx.status] || STATUS_META.BOZZA;
                return (
                  <div key={tx.transaction_id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-slate-50/60" data-testid={`sign-tx-row-${tx.transaction_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-slate-400">{tx.transaction_id}</span>
                        <Badge variant="outline" className={sm.cls} data-testid={`sign-tx-status-${tx.transaction_id}`}>{sm.label}</Badge>
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200" data-testid={`sign-tx-level-badge-${tx.transaction_id}`}>
                          {tx.verification_level === "OTP_ID" ? "OTP + Doc. identità" : "OTP"}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-700 flex-wrap">
                        <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4 text-slate-400" /> {tx.identity_name}</span>
                        <span className="inline-flex items-center gap-1.5"><FileText className="h-4 w-4 text-slate-400" /> {tx.document_title} <span className="text-xs text-slate-400">v{tx.document_version}</span></span>
                      </div>
                      {tx.document_sha256 && (
                        <div className="flex items-center gap-1.5 mt-1 text-[11px] text-emerald-700">
                          <ShieldCheck className="h-3 w-3 shrink-0" />
                          <span className="font-mono truncate">{tx.document_sha256}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-sky-600" onClick={() => openDetail(tx)} data-testid={`sign-tx-detail-${tx.transaction_id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {tx.status === "BOZZA" && (
                        <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" onClick={() => send(tx)} data-testid={`sign-tx-send-${tx.transaction_id}`}>
                          <Send className="h-4 w-4 mr-1" /> Attiva link
                        </Button>
                      )}
                      {tx.status === "IN_ATTESA" && (
                        <Button size="sm" variant="outline" onClick={() => copyLink(tx)} data-testid={`sign-tx-copylink-${tx.transaction_id}`}>
                          <Link2 className="h-4 w-4 mr-1" /> Copia link
                        </Button>
                      )}
                      {tx.status === "FIRMATA" && (
                        <Button size="sm" variant="outline" onClick={() => downloadSigned(tx)} data-testid={`sign-tx-download-signed-${tx.transaction_id}`}>
                          <ShieldCheck className="h-4 w-4 mr-1" /> Scarica firmato
                        </Button>
                      )}
                      {(tx.status === "BOZZA" || tx.status === "IN_ATTESA") && (
                        <Button size="sm" variant="ghost" className="text-slate-500 hover:text-orange-600" onClick={() => setCancelTarget(tx)} data-testid={`sign-tx-cancel-${tx.transaction_id}`}>
                          <Ban className="h-4 w-4 mr-1" /> Annulla
                        </Button>
                      )}
                      {tx.status !== "FIRMATA" && (
                        <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-500 hover:text-red-600" onClick={() => setDeleteTarget(tx)} data-testid={`sign-tx-delete-${tx.transaction_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="sign-tx-dialog">
          <DialogHeader>
            <DialogTitle>Nuova pratica di firma</DialogTitle>
            <DialogDescription>Collega un cliente (Identity) e un documento. Verrà creata una bozza, poi potrai attivare il link di firma.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 p-3">
              <Label className="text-xs text-slate-500 mb-1.5 block">Cliente (Identity)</Label>
              {identity ? (
                <div className="inline-flex items-center gap-1.5 text-sm text-slate-800">
                  <User className="h-4 w-4 text-sky-600" /> {displayIdentity(identity)} <span className="text-xs text-slate-400">({identity.identity_id})</span>
                  <button onClick={() => setIdentity(null)} className="text-slate-400 hover:text-red-600 ml-1"><X className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <Input value={idQuery} onChange={(e) => setIdQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchIdentity())} placeholder="Cerca cliente…" className="h-9" data-testid="sign-tx-identity-search" />
                    <Button type="button" variant="outline" className="h-9" onClick={searchIdentity} data-testid="sign-tx-identity-search-btn"><Search className="h-4 w-4" /></Button>
                  </div>
                  {idResults.length > 0 && (
                    <div className="mt-2 max-h-36 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
                      {idResults.map((it) => (
                        <button key={it.identity_id} type="button" onClick={() => { setIdentity(it); setIdResults([]); setIdQuery(""); }} className="w-full text-left px-3 py-2 hover:bg-sky-50 text-sm" data-testid={`sign-tx-identity-result-${it.identity_id}`}>
                          <div className="font-medium text-slate-800">{displayIdentity(it)}</div>
                          <div className="text-xs text-slate-400">{[it.codice_fiscale, it.email].filter(Boolean).join(" · ")}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 p-3">
              <Label className="text-xs text-slate-500 mb-1.5 block">Documento</Label>
              {doc ? (
                <div className="inline-flex items-center gap-1.5 text-sm text-slate-800">
                  <FileText className="h-4 w-4 text-sky-600" /> {doc.title} <span className="text-xs text-slate-400">({doc.document_id} v{doc.current_version})</span>
                  <button onClick={() => setDoc(null)} className="text-slate-400 hover:text-red-600 ml-1"><X className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <Input value={docQuery} onChange={(e) => setDocQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchDoc())} placeholder="Cerca documento…" className="h-9" data-testid="sign-tx-doc-search" />
                    <Button type="button" variant="outline" className="h-9" onClick={searchDoc} data-testid="sign-tx-doc-search-btn"><Search className="h-4 w-4" /></Button>
                  </div>
                  {docResults.length > 0 && (
                    <div className="mt-2 max-h-36 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
                      {docResults.map((d) => (
                        <button key={d.document_id} type="button" onClick={() => { setDoc(d); setDocResults([]); setDocQuery(""); }} className="w-full text-left px-3 py-2 hover:bg-sky-50 text-sm" data-testid={`sign-tx-doc-result-${d.document_id}`}>
                          <div className="font-medium text-slate-800">{d.title}</div>
                          <div className="text-xs text-slate-400">{d.document_id} · v{d.current_version}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <Label className="text-xs text-slate-500 mb-2 block">Livello di verifica del firmatario</Label>
            <div className="grid grid-cols-1 gap-2">
              <button type="button" onClick={() => setLevel("OTP")} className={`text-left rounded-lg border p-3 transition-colors ${level === "OTP" ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="sign-tx-level-OTP">
                <div className="flex items-center gap-2">
                  <span className={`h-4 w-4 rounded-full border-2 shrink-0 ${level === "OTP" ? "border-sky-500 bg-sky-500" : "border-slate-300"}`} />
                  <span className="font-medium text-slate-800 text-sm">OTP via SMS</span>
                </div>
                <p className="text-xs text-slate-500 mt-1 ml-6">Il cliente conferma con un codice ricevuto sul cellulare. Rapido e adatto alla maggior parte dei documenti.</p>
              </button>
              <button type="button" onClick={() => setLevel("OTP_ID")} className={`text-left rounded-lg border p-3 transition-colors ${level === "OTP_ID" ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="sign-tx-level-OTP_ID">
                <div className="flex items-center gap-2">
                  <span className={`h-4 w-4 rounded-full border-2 shrink-0 ${level === "OTP_ID" ? "border-sky-500 bg-sky-500" : "border-slate-300"}`} />
                  <span className="font-medium text-slate-800 text-sm">OTP + documento d'identità</span>
                </div>
                <p className="text-xs text-slate-500 mt-1 ml-6">Oltre all'OTP, il cliente carica un documento d'identità. Identificazione più robusta.</p>
              </button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="sign-tx-cancel-create">Annulla</Button>
            <Button onClick={create} disabled={creating} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="sign-tx-create">
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Crea bozza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link dialog */}
      <Dialog open={!!linkDialog} onOpenChange={(o) => !o && setLinkDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="sign-tx-link-dialog">
          <DialogHeader>
            <DialogTitle>Link di firma</DialogTitle>
            <DialogDescription>Condividi questo link con il cliente. La pagina pubblica di firma sarà attiva negli step successivi.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input readOnly value={linkDialog?.url || ""} className="font-mono text-xs" data-testid="sign-tx-link-value" />
            <Button variant="outline" onClick={async () => { const ok = await copyText(linkDialog.url); if (ok) toast.success("Link copiato"); }} data-testid="sign-tx-link-copy">
              <Copy className="h-4 w-4" />
            </Button>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 mt-2">
            <p className="text-xs text-slate-500 mb-2">Invia il link direttamente al cliente:</p>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={notifySms} onCheckedChange={(v) => setNotifySms(!!v)} data-testid="sign-tx-notify-sms" />
                <MessageSquare className="h-4 w-4 text-slate-400" /> Via SMS (al cellulare del cliente)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={notifyEmail} onCheckedChange={(v) => setNotifyEmail(!!v)} data-testid="sign-tx-notify-email" />
                <Mail className="h-4 w-4 text-slate-400" /> Via email (all'indirizzo del cliente)
              </label>
            </div>
            <Button onClick={notifyClient} disabled={notifying} className="w-full mt-3 bg-sky-600 hover:bg-sky-700 text-white" data-testid="sign-tx-notify-btn">
              {notifying ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />} Invia al cliente
            </Button>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 mt-2">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer mb-2">
              <Checkbox checked={remEnabled} onCheckedChange={(v) => setRemEnabled(!!v)} data-testid="sign-tx-rem-enabled" />
              <Clock className="h-4 w-4 text-slate-400" /> Promemoria automatico se non firma
            </label>
            {remEnabled && (
              <div className="flex items-end gap-3 pl-1">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Ogni (giorni)</label>
                  <Input type="number" min="1" value={remDays} onChange={(e) => setRemDays(e.target.value)} className="h-9 w-20" data-testid="sign-tx-rem-days" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Max invii</label>
                  <Input type="number" min="0" value={remMax} onChange={(e) => setRemMax(e.target.value)} className="h-9 w-20" data-testid="sign-tx-rem-max" />
                </div>
                <Button variant="outline" className="h-9" onClick={saveReminder} disabled={savingRem} data-testid="sign-tx-rem-save">
                  {savingRem ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
                </Button>
              </div>
            )}
            {!remEnabled && (
              <Button variant="outline" className="h-8 text-xs" onClick={saveReminder} disabled={savingRem} data-testid="sign-tx-rem-save">
                {savingRem ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disattiva promemoria"}
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setLinkDialog(null)} data-testid="sign-tx-link-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="sign-tx-detail-dialog">
          <DialogHeader>
            <DialogTitle>Dettaglio pratica {detail?.transaction?.transaction_id}</DialogTitle>
            <DialogDescription>Evidenze di firma, stato invio email e cronologia eventi.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-400 text-xs">Firmatario</span><div className="text-slate-800">{detail.transaction.identity_name}</div></div>
                <div><span className="text-slate-400 text-xs">Documento</span><div className="text-slate-800">{detail.transaction.document_title} v{detail.transaction.document_version}</div></div>
                <div><span className="text-slate-400 text-xs">Stato</span><div className="text-slate-800">{STATUS_META[detail.transaction.status]?.label || detail.transaction.status}</div></div>
                <div><span className="text-slate-400 text-xs">Livello</span><div className="text-slate-800">{detail.transaction.verification_level === "OTP_ID" ? "OTP + Doc. identità" : "OTP"}</div></div>
              </div>

              {detail.transaction.signature && (
                <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-3 text-xs text-emerald-800">
                  <div className="font-medium mb-1 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Firmato</div>
                  <div>Metodo: {detail.transaction.signature.method === "otp+id_document" ? "OTP + documento d'identità" : "OTP via SMS"}</div>
                  <div>Data: {detail.transaction.signed_at ? new Date(detail.transaction.signed_at).toLocaleString("it-IT") : "—"}</div>
                  {detail.transaction.signature.phone_masked && <div>Numero verificato: {detail.transaction.signature.phone_masked}</div>}
                  <div className="font-mono break-all mt-1">SHA-256: {detail.transaction.document_sha256}</div>
                </div>
              )}

              {detail.transaction.reminder && detail.transaction.status === "IN_ATTESA" && (
                <div className="text-xs text-slate-600" data-testid="sign-tx-detail-reminder">
                  <span className="text-slate-400">Promemoria automatici: </span>
                  {detail.transaction.reminder.enabled
                    ? `attivi ogni ${detail.transaction.reminder.days} giorni · inviati ${detail.transaction.reminder.sent}/${detail.transaction.reminder.max}`
                    : "disattivati"}
                </div>
              )}

              {detail.transaction.email_delivery && (
                <div className="text-xs text-slate-600">
                  <span className="text-slate-400">Invio email documento firmato: </span>
                  {detail.transaction.email_delivery.sent ? `✓ inviato a ${(detail.transaction.email_delivery.recipients || []).join(", ")}` : "non inviato"}
                </div>
              )}

              {detail.transaction.has_signed_document && (
                <Button size="sm" variant="outline" onClick={() => downloadSigned(detail.transaction)} data-testid="sign-tx-detail-download">
                  <ShieldCheck className="h-4 w-4 mr-1" /> Scarica documento firmato
                </Button>
              )}

              <EsitoSms txId={detail.transaction.transaction_id} />
              <div>
                <p className="text-xs text-slate-400 mb-2 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Cronologia</p>
                {detailLoading ? (
                  <div className="text-slate-400 text-xs flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Caricamento…</div>
                ) : (
                  <ul className="space-y-1.5" data-testid="sign-tx-detail-events">
                    {(detail.events || []).map((ev) => (
                      <li key={ev.event_id} className="flex items-center justify-between text-xs border-b border-slate-50 pb-1">
                        <span className="font-medium text-slate-700">{ev.action}</span>
                        <span className="text-slate-400">{ev.created_at ? new Date(ev.created_at).toLocaleString("it-IT") : ""}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)} data-testid="sign-tx-detail-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent data-testid="sign-tx-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Annullare la pratica?</AlertDialogTitle>
            <AlertDialogDescription>{cancelTarget && <>La pratica <strong>{cancelTarget.transaction_id}</strong> verrà annullata e l'eventuale link di firma non sarà più valido.</>}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sign-tx-cancel-no">No</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} className="bg-orange-600 hover:bg-orange-700" data-testid="sign-tx-cancel-yes">Annulla pratica</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="sign-tx-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la pratica?</AlertDialogTitle>
            <AlertDialogDescription>{deleteTarget && <>La pratica <strong>{deleteTarget.transaction_id}</strong> verrà eliminata definitivamente.</>}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sign-tx-delete-no">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="sign-tx-delete-yes">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
